import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { INBOX_TYPES, PROJECT_MEMBER_ROLES, routes } from 'src/app/constants';
import { SlackChannel } from 'src/models';
import { Project } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SlackService } from 'src/services/slack.service';

import { EditProjectComponent } from '../edit-project/edit-project.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, SharedPipesModule],
  selector: 'app-projects-list',
  templateUrl: './projects-list.component.html',
  styleUrls: ['./projects-list.component.scss'],
})
export class ProjectsListComponent implements OnInit, OnDestroy {
  projects: Partial<Project>[] = [];
  filteredProjects: Partial<Project>[] = [];

  searchText: string;
  isLoading = false;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  connectedChannels: { [key: string]: SlackChannel } = {};

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private projectsService: ProjectsService,
    private msgSrvc: MessageService,
    private slackService: SlackService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {}

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getProjects()]);

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        const modal = await this.modalCtrl.create({
          component: EditProjectComponent,
          componentProps: {
            id: params.id,
          },
        });

        modal.present();

        modal.onDidDismiss().then((data) => {
          this.getProjects();

          this.navCtrl
            .navigateForward(this.router.url, {
              queryParams: {},
            })
            .then(() => {
              // hacky way to be sure we update the dashboard to reflect a new project
              if (data.data?.action) {
                window.location.reload();
              }
            });
        });
      }
    });

    this.loadConnectedChannels();
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getProjects() {
    try {
      if (this.authService.isTenantAdmin) {
        const projectsResponse = await this.projectsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        });

        this.projects = projectsResponse.data;
      } else {
        const projectsResponse = await this.projectsService.listForUser(this.authService.userId, {
          limit: null,
          order: ['project.name'],
          descending: false,
          where: [
            {
              field: 'role',
              operator: '==',
              value: PROJECT_MEMBER_ROLES.OWNER,
            },
          ],
        });

        this.projects = projectsResponse.data.map((item: any) => item.project);
      }

      this.projects = this.appendFEProperties(this.projects);

      this.filteredProjects = this.projects;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  appendFEProperties(projects: Partial<Project>[]): Partial<Project>[] {
    return projects.map((project) => {
      const hasSupportTeamProps =
        project.supportLogo || project.supportName || (project.workingHours && project.workingHours.enabled);

      return {
        ...project,
        hasSupportTeamProps,
      };
    });
  }

  async loadConnectedChannels() {
    try {
      const { channels: connectedChannels } = await this.slackService.getConnectedChannels();

      this.connectedChannels = connectedChannels.reduce((acc, c) => {
        acc[c.id] = c;

        return acc;
      }, {});
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  channelName(channelId: string) {
    return this.connectedChannels[channelId] ? this.connectedChannels[channelId].displayName : '';
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredProjects = this.projects;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredProjects = this.projects.filter((project) => project.name.toLowerCase().includes(term));
    }
  }

  async createProject() {
    const modal = await this.modalCtrl.create({
      component: EditProjectComponent,
      componentProps: {},
    });

    modal.present();

    modal.onDidDismiss().then((data) => {
      this.getProjects();

      // hacky way to be sure we update the dashboard to reflect a new project
      if (data.data?.action) {
        window.location.reload();
      }
    });
  }

  async editProject(id: string) {
    this.navCtrl.navigateRoot(`/dashboard/settings/projects/${id}/settings`);
  }

  async viewProject(project: Partial<Project>) {
    this.authService.storeSelectedProject(project);
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`);
  }
}
